import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useRegisterModal } from '../../contexts/RegisterModalContext';
import { useLanguage } from '../../contexts/LanguageContext';

const LANGUAGES = [
  { value: 'ru', label: 'Русский', flagSrc: '/flags/ru.svg' },
  { value: 'tt', label: 'Татарский', flagSrc: '/flags/tt.svg' },
  { value: 'ba', label: 'Башкирский', flagSrc: '/flags/ba.svg' },
  { value: 'ce', label: 'Чеченский', flagSrc: '/flags/ce.svg' },
  { value: 'cv', label: 'Чувашский', flagSrc: '/flags/cv.svg' },
];

function LanguageDropdown({
  id,
  value,
  onChange,
  className = '',
  fullWidth = false,
  onAfterSelect,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = useMemo(() => {
    return LANGUAGES.find((l) => l.value === value) ?? LANGUAGES[0];
  }, [value]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (e) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectLanguage = (next) => {
    onChange?.(next);
    setOpen(false);
    onAfterSelect?.();
  };

  return (
    <div ref={rootRef} className={`relative ${fullWidth ? 'w-full' : ''} ${className}`.trim()}>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`
          inline-flex items-center gap-2
          ${fullWidth ? 'w-full' : ''}
          h-10
          rounded-full
          border border-slate-200
          bg-white/70 backdrop-blur
          px-3
          text-sm font-semibold text-slate-800
          shadow-sm
          hover:bg-white hover:border-slate-300
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40
          transition-all
        `.trim()}
      >
        <img
          src={selected.flagSrc}
          alt=""
          className="h-4 w-6 rounded-[2px] border border-slate-200 bg-white shadow-sm"
          aria-hidden="true"
        />
        <span className="truncate">{selected.label}</span>
        <span className="ml-auto text-slate-500" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-labelledby={id}
          className={`
            absolute right-0 mt-2 z-50
            ${fullWidth ? 'w-full' : 'w-56'}
            overflow-hidden
            rounded-2xl
            border border-slate-200
            bg-white/90 backdrop-blur-xl
            shadow-2xl
          `.trim()}
        >
          {LANGUAGES.map((l) => {
            const active = l.value === selected.value;

            return (
              <button
                key={l.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => selectLanguage(l.value)}
                className={`
                  flex w-full items-center gap-2 px-3 py-2 text-left
                  text-sm font-semibold
                  ${active ? 'bg-sky-100 text-slate-900' : 'text-slate-800 hover:bg-slate-100'}
                `.trim()}
              >
                <img
                  src={l.flagSrc}
                  alt=""
                  className="h-4 w-6 rounded-[2px] border border-slate-200 bg-white shadow-sm"
                  aria-hidden="true"
                />
                <span className="truncate">{l.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const NAV_LINKS = [
  { labelKey: 'nav.home', to: '/' },
  { labelKey: 'nav.models', to: '/models' },
  { labelKey: 'nav.actresses', to: '/actresses' },
  { labelKey: 'nav.dancers', to: '/dancers' },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { openRegister } = useRegisterModal();
  const { language, setLanguage, t } = useLanguage();
  const [visitorName, setVisitorName] = useState('');

  useEffect(() => {
    try {
      const n = localStorage.getItem('name') || localStorage.getItem('visitorName') || '';
      setVisitorName(n);
    } catch (e) {
      setVisitorName('');
    }
  }, []);

  const onLanguageChange = (next) => {
    if (!LANGUAGES.some((l) => l.value === next)) {
      setLanguage('ru');
      return;
    }
    setLanguage(next);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/70 backdrop-blur-xl">
      <div className="flex items-center justify-between h-14 xs:h-16 px-3 xs:px-4 tablet:px-6">
        <Link to="/" className="flex items-center gap-0 xs:gap-0 group">
          <span className="inline-flex items-center justify-center h-12 w-12 xs:h-16 xs:w-[9rem] tablet:h-[7rem] tablet:w-[10rem] -mr-[45px]">
            <img
              src="/logo.png"
              alt="QueenVote"
              className="h-full w-full object-contain select-none pointer-events-none drop-shadow"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/logo.svg';
              }}
            />
          </span>
          <span className="-ml-1 text-base tablet:text-lg font-black bg-gradient-to-r from-sky-600 to-blue-700 text-transparent bg-clip-text group-hover:from-sky-500 group-hover:to-blue-600 transition-all whitespace-nowrap">
            QueenVote
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden tablet:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`
                px-3 laptop:px-4 py-2 rounded-full text-xs laptop:text-sm font-medium transition-all
                ${isActive(link.to)
                  ? 'bg-sky-100 text-slate-900'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }
              `}
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden tablet:flex items-center gap-1 laptop:gap-2">
          <LanguageDropdown
            id="header-language"
            value={language}
            onChange={onLanguageChange}
          />

          <button
            type="button"
            onClick={openRegister}
            className="
              inline-flex items-center gap-1 laptop:gap-2
              px-2 laptop:px-4 py-2 rounded-full text-xs laptop:text-sm font-bold
              bg-white/70 border border-slate-200 text-slate-800
              hover:bg-white hover:border-slate-300
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40
              transition-all
            "
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-slate-700"
              aria-hidden="true"
            >
              <path
                d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z"
                fill="currentColor"
              />
            </svg>
            {visitorName ? `${t('header.register')}, ${visitorName}` : t('header.register')}
          </button>

          <button
            type="button"
            onClick={() => openRegister('Чтобы пользоваться функциями сайта, нужно зарегистрироваться')}
            className="px-3 laptop:px-4 py-2 rounded-full text-xs laptop:text-sm font-bold bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-sm hover:shadow-sky-500/20 hover:scale-105 transition-all"
          >
            {t('header.vote')}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="tablet:hidden flex flex-col justify-center items-center w-9 h-9 xs:w-10 xs:h-10 rounded-lg bg-white/70 border border-slate-200"
          aria-label="Toggle menu"
        >
          <span className={`w-4 xs:w-5 h-0.5 bg-slate-800 transition-all ${isMenuOpen ? 'rotate-45 translate-y-1' : ''}`} />
          <span className={`w-4 xs:w-5 h-0.5 bg-slate-800 my-1 ${isMenuOpen ? 'opacity-0' : ''}`} />
          <span className={`w-4 xs:w-5 h-0.5 bg-slate-800 transition-all ${isMenuOpen ? '-rotate-45 -translate-y-1' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`
          tablet:hidden overflow-hidden transition-all duration-300 ease-in-out
          ${isMenuOpen ? 'max-h-96 border-t border-slate-200' : 'max-h-0'}
        `}
      >
        <nav className="flex flex-col p-3 xs:p-4 gap-2">
          <div className="px-1">
            <LanguageDropdown
              id="mobile-language"
              value={language}
              onChange={onLanguageChange}
              fullWidth
              onAfterSelect={() => setIsMenuOpen(false)}
            />
          </div>

          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setIsMenuOpen(false)}
              className={`
                px-3 xs:px-4 py-2 xs:py-3 rounded-lg xs:rounded-xl text-sm font-medium transition-all
                ${isActive(link.to)
                  ? 'bg-sky-100 text-slate-900'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }
              `}
            >
              {t(link.labelKey)}
            </Link>
          ))}

          <button
            type="button"
            onClick={() => {
              setIsMenuOpen(false);
              openRegister();
            }}
            className="
              mt-2 px-4 py-3 rounded-xl text-sm font-bold text-center
              bg-white/70 border border-slate-200 text-slate-800
              hover:bg-white hover:border-slate-300
              inline-flex items-center justify-center gap-2
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40
              transition-all
            "
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-slate-700"
              aria-hidden="true"
            >
              <path
                d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z"
                fill="currentColor"
              />
            </svg>
            {visitorName ? `${t('header.register')}, ${visitorName}` : t('header.register')}
          </button>

          <button
            type="button"
            onClick={() => {
              setIsMenuOpen(false);
              openRegister('Чтобы пользоваться функциями сайта, нужно зарегистрироваться');
            }}
            className="mt-2 px-4 py-3 rounded-xl text-sm font-bold text-center bg-gradient-to-r from-sky-500 to-blue-600 text-white"
          >
            {t('header.vote')}
          </button>
        </nav>
      </div>
    </header>
  );
}
